import { FormTitle } from "@/entity/tax-accountant";
import { IBelongToTaxAccountantResModel, ITaxCorporationDetailResModel, useGetTaxCorporation } from "@/shared/api";
import { vars } from "@/shared/lib";
import { Button, Column, Row, Table, Typography } from "@/shared/ui";
import { createFileRoute, useParams, useRouter } from "@tanstack/react-router";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import dayjs from "dayjs";
import { pageContainer, tableScrollContainer, tableWrapper } from "@/pages/tax-corporation/taxCorporationDetail.css";

export const Route = createFileRoute("/(auth)/_layout/tax-corporation/$taxCorporationId")({
	component: TaxCorporationDetail
});

function TaxCorporationDetail() {
	const { taxCorporationId } = useParams({ from: "/(auth)/_layout/tax-corporation/$taxCorporationId" });
	const router = useRouter();

	const {
		data: { taxAccountants, ...taxCorporation }
	} = useGetTaxCorporation({ taxCorporationId });

	return (
		<Column
			className={pageContainer}
			style={{
				justifyContent: "flex-start",
				alignItems: "flex-start",
				gap: vars.size[48]
			}}
		>
			<TaxCorporationGrid data={[taxCorporation]} />
			<TaxAccountantsGrid data={taxAccountants || []} />
			<Row
				fullWidth
				style={{ justifyContent: "center", paddingBlock: vars.size[32], paddingInline: vars.size[16], gap: vars.size[8] }}
			>
				<Button color={"third"} size="large" onClick={() => router.history.back()}>
					{"뒤로가기"}
				</Button>
			</Row>
		</Column>
	);
}

const taxCorporationColumnHelper = createColumnHelper<Omit<ITaxCorporationDetailResModel, "taxAccountants">>();
const taxCorporationColumnHelperColumns = [
	taxCorporationColumnHelper.accessor("manualUpdateDate", {
		header: () => "업데이트 일자",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue() ? dayjs(info.getValue()).format("YYYY-MM-DD") : ""}
			</Typography>
		),
		size: 120
	}),
	taxCorporationColumnHelper.accessor("affiliation", {
		header: () => "소속 지방청명",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue()}
			</Typography>
		),
		size: 120
	}),
	taxCorporationColumnHelper.accessor("name", {
		header: () => "법인명",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue()}
			</Typography>
		),
		size: 120
	}),
	taxCorporationColumnHelper.accessor("representativeName", {
		header: () => "대표",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue()}
			</Typography>
		),
		size: 120
	}),
	taxCorporationColumnHelper.accessor("telephone", {
		header: () => "전화번호",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue()}
			</Typography>
		),
		size: 120
	}),
	taxCorporationColumnHelper.accessor("fax", {
		header: () => "Fax",
		cell: (info) => {
			return (
				<Typography size={vars.size[14]} fontWeight="normal" inline>
					{info.getValue()}
				</Typography>
			);
		},
		size: 120
	}),
	taxCorporationColumnHelper.accessor("address", {
		header: () => "주소",
		cell: (info) => {
			return (
				<Typography size={vars.size[14]} fontWeight="normal" style={{ textAlign: "left" }}>
					{info.getValue()}
				</Typography>
			);
		},
		size: 450
	}),
	taxCorporationColumnHelper.accessor("homepageUrl", {
		header: () => "홈페이지",
		cell: (info) => {
			return (
				<Typography size={vars.size[14]} fontWeight="normal" inline>
					{info.getValue()}
				</Typography>
			);
		},
		size: 120
	}),
	taxCorporationColumnHelper.accessor("branchCount", {
		header: () => "지사개수",
		cell: (info) => {
			return (
				<Typography size={vars.size[14]} fontWeight="normal" inline>
					{info.getValue()}
				</Typography>
			);
		},
		size: 120
	}),
	taxCorporationColumnHelper.accessor("registeredTaxAccountantCont", {
		header: () => "등록 세무사",
		cell: (info) => {
			return (
				<Typography size={vars.size[14]} fontWeight="normal" inline>
					{info.getValue()}
				</Typography>
			);
		},
		size: 120
	}),
	taxCorporationColumnHelper.accessor("unregisteredTaxAccountantCont", {
		header: () => "미등록 세무사",
		cell: (info) => {
			return (
				<Typography size={vars.size[14]} fontWeight="normal" inline>
					{info.getValue()}
				</Typography>
			);
		},
		size: 120
	}),
	// taxCorporationColumnHelper.accessor("", {
	// 	header: () => "등록연월",
	// 	cell: (info) => {
	// 		return (
	// 			<Typography size={vars.size[14]} fontWeight="normal" inline>
	// 				{info.getValue()}
	// 			</Typography>
	// 		);
	// 	},
	// 	size: 120
	// })
	taxCorporationColumnHelper.accessor("etc", {
		header: () => "비고",
		cell: (info) => {
			return (
				<Typography size={vars.size[14]} fontWeight="normal" inline>
					{info.getValue()}
				</Typography>
			);
		},
		size: 120
	})
];

function TaxCorporationGrid({ data }: { data: Omit<ITaxCorporationDetailResModel, "taxAccountants">[] }) {
	const table = useReactTable({
		data: data,
		columns: taxCorporationColumnHelperColumns,
		enableRowSelection: true,
		enableMultiRowSelection: true,
		getCoreRowModel: getCoreRowModel(),

		debugTable: false,
		debugHeaders: false,
		debugColumns: false,
		debugCells: false,
		debugRows: false
	});

	return (
		<Column fullWidth className={tableWrapper}>
			<FormTitle title={"법인 정보"} />

			<Row fullWidth className={tableScrollContainer} style={{ padding: vars.size[4] }}>
				<Table style={{ width: table.getTotalSize() }}>
					<Table.Header>
						{table.getHeaderGroups().map(({ id, headers }) => (
							<Table.TR key={id}>
								{headers.map(({ getContext, column, id }) => (
									<Table.TH key={id} style={{ width: column.getSize() }} onClick={column.getToggleSortingHandler()}>
										{flexRender(column.columnDef.header, getContext())}
									</Table.TH>
								))}
							</Table.TR>
						))}
					</Table.Header>
					<Table.Body>
						{table.getRowModel().rows.map(({ id, getVisibleCells }) => (
							<Table.TR key={id}>
								{getVisibleCells().map(({ id, column, getContext }) => (
									<Table.TD key={id} style={{ width: column.getSize() }}>
										{flexRender(column.columnDef.cell, getContext())}
									</Table.TD>
								))}
							</Table.TR>
						))}
					</Table.Body>
				</Table>
			</Row>
		</Column>
	);
}

const belongToTaxAccountantColumnHelper = createColumnHelper<IBelongToTaxAccountantResModel>();
const belongToTaxAccountantColumns = [
	belongToTaxAccountantColumnHelper.accessor("manualUpdateDate", {
		header: () => "업데이트 일자",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue() ? dayjs(info.getValue()).format("YYYY-MM-DD") : ""}
			</Typography>
		),
		size: 120
	}),
	belongToTaxAccountantColumnHelper.accessor("name", {
		header: () => "성명",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue()}
			</Typography>
		),
		size: 120
	}),
	belongToTaxAccountantColumnHelper.accessor("belongTo", {
		header: () => "소속",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue()}
			</Typography>
		),
		size: 120
	}),
	belongToTaxAccountantColumnHelper.accessor("telephone", {
		header: () => "전화번호",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" inline>
				{info.getValue()}
			</Typography>
		),
		size: 120
	}),
	belongToTaxAccountantColumnHelper.accessor("jibunAddress", {
		header: () => "주소",
		cell: (info) => (
			<Typography size={vars.size[14]} fontWeight="normal" style={{ textAlign: "left" }}>
				{info.getValue()}
			</Typography>
		),
		size: 450
	}),
	belongToTaxAccountantColumnHelper.accessor("managerInformation", {
		header: () => "담당자",
		cell: (info) => {
			const value = info.getValue();
			return (
				<Typography size={vars.size[14]} fontWeight="normal" inline>
					{value?.name}
				</Typography>
			);
		},
		size: 120
	}),
	// belongToTaxAccountantColumnHelper.accessor("", {
	// 	header: () => "해촉사유",
	// 	cell: (info) => (
	// 		<Typography size={vars.size[14]} fontWeight="normal" inline>
	// 			{info.getValue()}
	// 		</Typography>
	// 	),
	// 	size: 120
	// }),
	belongToTaxAccountantColumnHelper.accessor("etc", {
		header: () => "상태",
		cell: (info) => {
			return (
				<Typography size={vars.size[14]} fontWeight="normal" style={{ textAlign: "left" }}>
					{info.getValue()}
				</Typography>
			);
		},
		size: 120
	})
];

function TaxAccountantsGrid({ data }: { data: IBelongToTaxAccountantResModel[] }) {
	const table = useReactTable({
		data: data,
		columns: belongToTaxAccountantColumns,
		enableRowSelection: true,
		enableMultiRowSelection: true,
		getCoreRowModel: getCoreRowModel(),

		debugTable: false,
		debugHeaders: false,
		debugColumns: false,
		debugCells: false,
		debugRows: false
	});

	return (
		<Column fullWidth className={tableWrapper}>
			<FormTitle title={"법인 구성원 정보"} />

			<Row fullWidth className={tableScrollContainer} style={{ padding: vars.size[4] }}>
				<Table style={{ width: table.getTotalSize() }}>
					<Table.Header>
						{table.getHeaderGroups().map(({ id, headers }) => (
							<Table.TR key={id}>
								{headers.map(({ getContext, column, id }) => (
									<Table.TH key={id} style={{ width: column.getSize() }} onClick={column.getToggleSortingHandler()}>
										{flexRender(column.columnDef.header, getContext())}
									</Table.TH>
								))}
							</Table.TR>
						))}
					</Table.Header>
					<Table.Body>
						{table.getRowModel().rows.map(({ id, getVisibleCells }) => (
							<Table.TR key={id}>
								{getVisibleCells().map(({ id, column, getContext }) => (
									<Table.TD key={id} style={{ width: column.getSize() }}>
										{flexRender(column.columnDef.cell, getContext())}
									</Table.TD>
								))}
							</Table.TR>
						))}
					</Table.Body>
				</Table>
			</Row>
		</Column>
	);
}
